import { AlertTriangle, Ban, Building2, CheckCircle2, Network, RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { ErrorState } from '../../../../components/ErrorState';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import { budgetDepartmentsApi } from '../budgetDepartmentsApi';
import type { BudgetDepartmentListItem } from '../budgetDepartmentTypes';

type TabKey = 'info';

/**
 * One enrolled budget department.
 *
 * **There is no Edit anywhere on this page**, and the absence is the design: the row holds a
 * link to an org unit and nothing else, so everything the profile shows belongs to the
 * Organization module. That is the third case in `<ProfileCard>`'s exemption list — a card
 * over ANOTHER module's record links out to that record instead of editing it in place — and
 * the enrolment's own lifecycle is the banner's Withdraw and Restore.
 */
export function BudgetDepartmentProfilePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [department, setDepartment] = useState<BudgetDepartmentListItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [running, setRunning] = useState(false);
  const [tab, setTab] = useState<TabKey>('info');

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      setLoading(true);

      budgetDepartmentsApi
        .get(id, signal)
        .then((next) => {
          if (signal?.aborted) return;

          setDepartment(next);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;

          setError(
            caught instanceof ApiError
              ? caught
              : new ApiError('Could not load this budget department.', 0),
          );
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  async function run(action: 'withdraw' | 'restore') {
    if (!id) return;

    setRunning(true);
    setError(null);

    try {
      if (action === 'withdraw') {
        await budgetDepartmentsApi.deactivate(id);
      } else {
        await budgetDepartmentsApi.activate(id);
      }

      load();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught
          : new ApiError('The action could not be completed.', 0),
      );
    } finally {
      setRunning(false);
    }
  }

  const breadcrumb: BreadcrumbSegment[] = [
    'Procurement',
    'Investment Planning',
    { label: 'Settings', to: '/procurement/investment-planning/settings' },
    {
      label: 'Budget Departments',
      to: '/procurement/investment-planning/settings/budget-departments',
    },
    department?.orgUnitName ?? 'Budget department',
  ];

  if (error && !department) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState
          message={error.message}
          correlationId={error.correlationId}
          onRetry={() => load()}
        />
      </div>
    );
  }

  if (loading || !department) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="budget-department-profile"
        header={
          <ProfileHeader
            id="budget-department-profile-header"
            icon={<Building2 className="h-6 w-6" aria-hidden="true" />}
            title={department.orgUnitName}
            /*
              The unit's own CODE and TIER — two facts about the office, neither of which is
              the title beside them. Each is dropped where the unit carries none rather than
              rendering an em dash into a middot-joined line.
            */
            meta={[
              ...(department.orgUnitCode ? [department.orgUnitCode] : []),
              ...(department.orgUnitTypeName ? [department.orgUnitTypeName] : []),
            ]}
            status={
              /*
                Three states, not two — see the index page's Status column. A withdrawn
                enrolment and an enrolment whose office has left the chart are different
                facts, and only the second is something nobody chose.
              */
              !department.isActive ? (
                <StatusText tone="inactive">Withdrawn</StatusText>
              ) : department.isOrgUnitActive ? (
                <StatusText tone="active">Enrolled</StatusText>
              ) : (
                <StatusText tone="blocked">Unit deleted</StatusText>
              )
            }
            createdBy={department.createdByName}
            createdAt={department.createdAt}
          />
        }
        banner={
          error ? (
            <ProfileBanner
              id="budget-department-profile-banner"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message={error.message}
            />
          ) : !department.isActive ? (
            <ProfileBanner
              id="budget-department-profile-banner"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message="This department is withdrawn. It is off every plan's roster and no longer counts toward how many offices are expected to propose — the PPAs it already filed are untouched."
              action={
                <PermissionGate permission="budget-departments.delete">
                  <Button
                    id="budget-department-profile-restore"
                    variant="secondary"
                    leading={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => void run('restore')}
                    disabled={running}
                    loading={running}
                    loadingLabel="Restoring"
                  >
                    Restore
                  </Button>
                </PermissionGate>
              }
            />
          ) : !department.isOrgUnitActive ? (
            /*
              A roster row pointing at an office that has left the chart. It is NOT withdrawn
              automatically — a deleted catalog row is left alone where it is already
              recorded — so the page says what is true and offers the withdrawal rather than
              taking it.
            */
            <ProfileBanner
              id="budget-department-profile-banner"
              icon={<AlertTriangle className="h-4 w-4" aria-hidden="true" />}
              message="The organizational unit behind this enrolment has been deleted from the chart. The department still appears on every plan's roster — restore the unit on the chart, or withdraw the department."
              action={
                <PermissionGate permission="budget-departments.delete">
                  <Button
                    id="budget-department-profile-withdraw"
                    variant="secondary"
                    leading={<Ban className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => void run('withdraw')}
                    disabled={running}
                    loading={running}
                    loadingLabel="Withdrawing"
                  >
                    Withdraw
                  </Button>
                </PermissionGate>
              }
            />
          ) : (
            <ProfileBanner
              id="budget-department-profile-banner"
              icon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
              message="This department is on every plan's roster, whether or not it has proposed anything — which is what lets a plan say how many offices have started out of how many there are."
              action={
                <PermissionGate permission="budget-departments.delete">
                  <Button
                    id="budget-department-profile-withdraw"
                    variant="secondary"
                    leading={<Ban className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => void run('withdraw')}
                    disabled={running}
                    loading={running}
                    loadingLabel="Withdrawing"
                  >
                    Withdraw
                  </Button>
                </PermissionGate>
              }
            />
          )
        }
        tabs={[{ key: 'info', label: 'Info' }]}
        activeTab={tab}
        onTabChange={setTab}
      >
        <ProfileCard
          id="budget-department-unit"
          title="Organizational unit"
          /*
            The cog LINKS OUT rather than editing: the name, the code and the tier all belong
            to the Organization module and are corrected on its chart. The entry carries
            `org-units.read` — the key of what it OPENS, never this page's own — and is hidden
            without it.
          */
          menuItems={[
            {
              key: 'chart',
              label: 'Open the organizational chart',
              icon: <Network className="h-4 w-4" aria-hidden="true" />,
              permission: 'org-units.read',
              onClick: () => navigate('/organization/chart'),
            },
          ]}
        >
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Unit', value: department.orgUnitName },
              // A PATH through the chart. Mono because it is an identifier, never `numeric` —
              // nothing in `0001-2-3` should dim.
              { label: 'Unit code', value: department.orgUnitCode, mono: true },
              { label: 'Tier', value: department.orgUnitTypeName },
              {
                label: 'In the chart',
                value: department.isOrgUnitActive ? 'Yes' : 'No — the unit has been deleted',
              },
            ]}
          />
        </ProfileCard>

        <ProfileCard id="budget-department-proposals" title="Proposals">
          {/*
            No cog: nothing on this card is a field. The count is derived from the PPAs filed
            across every plan, and the way to change it is to file or withdraw one — which
            happens on the plan, not here.
          */}
          <PreviewTable
            variant="table-display"
            rows={[
              {
                label: 'PPAs filed',
                value: String(department.ppaCount),
                mono: true,
              },
            ]}
          />
        </ProfileCard>

        <ProfileCard id="budget-department-audit" title="Audit">
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Created by', value: department.createdByName },
              {
                label: 'Created',
                value: (
                  <time dateTime={toIso(department.createdAt)} className="font-mono">
                    {formatDateTime(department.createdAt)}
                  </time>
                ),
              },
              { label: 'Updated by', value: department.updatedByName },
              {
                label: 'Updated',
                value: department.updatedAt ? (
                  <time dateTime={toIso(department.updatedAt)} className="font-mono">
                    {formatDateTime(department.updatedAt)}
                  </time>
                ) : null,
              },
            ]}
          />
        </ProfileCard>
      </ProfileHeaderShell>
    </div>
  );
}
